/** @format */

import type React from "react";
import type { FC } from "react";

import { httpsCallable } from "firebase/functions";
import { type FormikValues, useFormik } from "formik";
import { useTranslation } from "react-i18next";
import {
  cloudFunctions,
  generateErrorMessage,
  useAuthContext,
  useSchemaValidation,
} from "senf-shared";

import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import type { FormProps } from "../../core/inputs/Form";
import Form from "../../core/inputs/Form";
import { useModals } from "../../molecules/modalStack/ModalProvider";

interface AuthResetPasswordProps {
  formikStore: FormikValues;
  className?: string;
  setResetOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

const AuthResetPassword: FC<AuthResetPasswordProps> = ({
  formikStore: formikStoreInitial,
  className,
  setResetOpen,
}) => {
  const { t } = useTranslation();

  const { loading, setLoading } = useAuthContext();
  const { closeModal, openModal } = useModals();

  const onPasswordReset = httpsCallable(cloudFunctions, "onPasswordReset");

  const { email } = useSchemaValidation();

  const inputItems: FormProps["inputItems"] = [
    { name: "email", type: "email", placeholder: "E-Mail" },
  ];

  const formikStore = useFormik({
    initialValues: formikStoreInitial?.values,
    validationSchema: email,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  const options = {
    submitText: t("global:reset"),
    submitDisabled: !formikStore.isValid,
    submitLoading: loading === "reset",
    onSubmit: async () => {
      setLoading("reset");
      try {
        const res = await onPasswordReset({
          email: formikStore.values?.email,
          url: `${window.location.origin}/resetPassword`,
          continueUrl: window.location.href,
        });
        closeModal({
          id: "authResetPassword",
        });
        setLoading(false);
        setResetOpen?.(false);
        openModal(null, {
          id: "resetEmailSent",
          title: t("global:emailSent"),
          description: t("auth:emailSentDescription"),
          cancelText: "OK",
        });
        return res;
      } catch (e) {
        setLoading(false);
        openModal(null, {
          id: "resetEmailError",
          title: t(generateErrorMessage(e.message)),
          description: t("auth:tryAgainLater"),
          cancelText: "OK",
        });
      }
    },
  };

  return (
    <div className={cn("flex flex-col gap-3 w-full p-5", className)}>
      <Form inputItems={inputItems} formik={formikStore} />
      <Button
        size="lg"
        id="submitButton"
        variant="primary"
        width="max"
        onClick={options?.onSubmit}
        disabled={!!options?.submitDisabled}
        text={options?.submitText}
        loading={options?.submitLoading}
      />
    </div>
  );
};

export default AuthResetPassword;
